import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { EmptyState, Spinner, ThemeProvider, ToastProvider, navigate, tabAccount, useLocation } from '@dugzi/ui';
import { APP, api } from './lib/api';
import type { Me, Usage } from './lib/types';
import { Shell } from './components/Shell';
import { NeedAccess } from './components/NeedAccess';

const PublicPage = lazy(() => import('./components/PublicPage'));

/** Dugzi Drive on its own (D-031, D-034): works with any Dugzi Account. */
export function App() {
  const { path } = useLocation();
  if (path.startsWith('/s/')) {
    return (
      <ThemeProvider initial={null}>
        <ToastProvider>
          <Suspense fallback={<div className="boot"><Spinner label="Opening" /></div>}>
            <PublicPage token={path.slice(3).split('/')[0]} />
          </Suspense>
        </ToastProvider>
      </ThemeProvider>
    );
  }
  return <SignedIn />;
}

function SignedIn() {
  const [me, setMe] = useState<Me | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [needAccess, setNeedAccess] = useState<string | null>(null);
  const { path } = useLocation();
  useEffect(() => {
    tabAccount.adoptFromUrl(APP);
    api<Me>('/api/me').then(setMe).catch((e) => { if (e.status !== 401) setFailed(e.message); });
  }, []);
  // A file link (/f/fil_…, from Mail or the API's webUrl): open its folder with the file previewed.
  useEffect(() => {
    const m = /^\/f\/(fil_[a-z0-9]+)/.exec(path);
    if (!m || !me) return;
    api<{ parentId: string | null }>(`/api/nodes/${m[1]}`)
      .then((n) => navigate(`${n.parentId && n.parentId !== me.rootId ? `/folders/${n.parentId}` : '/'}?preview=${m[1]}`, true))
      // No access and no such file answer the same 404: a page that says so, not an outage (DZ-10803).
      .catch((e) => { if (e.status === 404) setNeedAccess(m[1]); else setFailed(e.message); });
  }, [path, me]);
  const setUsage = useCallback((u: Usage) => setMe((m) => (m ? { ...m, usage: u } : m)), []);
  const initial = useMemo(() => (me ? { appearance: me.user.appearance as never, accent: me.user.accent as never } : null), [me?.user.appearance, me?.user.accent]); // eslint-disable-line react-hooks/exhaustive-deps

  if (failed) return <div className="boot"><EmptyState scene="failure" title="Dugzi Drive can't open this right now">{failed}</EmptyState></div>;
  if (me && needAccess && path.startsWith('/f/')) return <ToastProvider><NeedAccess me={me} kind="file" id={needAccess} /></ToastProvider>;
  if (!me || path.startsWith('/f/')) return <div className="boot"><Spinner label="Opening Dugzi Drive" /></div>;
  return (
    <ThemeProvider initial={initial}>
      <ToastProvider>
        <Suspense fallback={null}>
          <Shell me={me} setUsage={setUsage} />
        </Suspense>
      </ToastProvider>
    </ThemeProvider>
  );
}
